import { hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import RomeCalendar from 'garaje/components/rome-calendar';
import moment from 'moment-timezone';

const copyTime = ({ from, to }: { from: Date | null; to: Date | null }) => {
  const momentFrom = moment(from);
  const momentTo = moment(to);

  if (from) {
    /* eslint-disable ember/use-ember-get-and-set */
    momentTo.set('hour', momentFrom.get('hour'));
    momentTo.set('minute', momentFrom.get('minute'));
    momentTo.set('second', momentFrom.get('second'));
    momentTo.set('millisecond', momentFrom.get('millisecond'));
    /* eslint-enable ember/use-ember-get-and-set */
  }

  return momentTo.toDate();
};

interface DatetimePickerSignature {
  Args: {
    minDate?: Date;
    maxDate?: Date;
    minTime?: Date;
    maxTime?: Date;
    value: Date;
    update: (date: Date | null) => void;
    timezone?: string;
    disabled?: boolean;
    isReadonly?: boolean;
    dateFormat?: string;
  };
  Blocks: {
    default: [
      {
        datePicker: WithBoundArgs<
          typeof RomeCalendar,
          | 'format'
          | 'max'
          | 'min'
          | 'timezone'
          | 'showDate'
          | 'showTime'
          | 'time'
          | 'timeSetter'
          | 'disabled'
          | 'isReadonly'
        >;
        timePicker: WithBoundArgs<
          typeof RomeCalendar,
          | 'format'
          | 'max'
          | 'min'
          | 'timezone'
          | 'showDate'
          | 'showTime'
          | 'time'
          | 'timeSetter'
          | 'disabled'
          | 'isReadonly'
        >;
      },
    ];
  };
}

/**
 * @deprecated Use non Rome based flavors of date and time pickers
 */
export default class DatetimePicker extends Component<DatetimePickerSignature> {
  get dateFormatValue(): string {
    return this.args.dateFormat || 'dddd, MMM D, YYYY';
  }

  @action
  updateDate(oldDateTime: Date | null, newDate: Date | null): void {
    let newDateTime;

    if (oldDateTime === null && newDate === null) {
      newDateTime = null;
    } else {
      newDateTime = copyTime({ to: newDate, from: oldDateTime });
    }

    this.args.update(newDateTime);
  }

  @action
  updateTime(oldDateTime: Date | null, newTime: Date | null): void {
    let newDateTime;

    if (oldDateTime === null && newTime === null) {
      newDateTime = null;
    } else if (oldDateTime) {
      newDateTime = copyTime({ to: oldDateTime, from: newTime });
    } else {
      newDateTime = newTime;
    }

    this.args.update(newDateTime);
  }

  <template>
    {{yield
      (hash
        datePicker=(component
          RomeCalendar
          format=this.dateFormatValue
          max=@maxDate
          min=@minDate
          timezone=@timezone
          showDate=true
          showTime=false
          time=@value
          timeSetter=this.updateDate
          disabled=@disabled
          isReadonly=@isReadonly
        )
        timePicker=(component
          RomeCalendar
          format="h:mm a"
          max=@maxTime
          min=@minTime
          timezone=@timezone
          showDate=false
          showTime=true
          time=@value
          timeSetter=this.updateTime
          disabled=@disabled
          isReadonly=@isReadonly
        )
      )
    }}
  </template>
}
